/**
 * Province center coordinates for automatic map zooming
 * Approximate center points for each Dutch province
 */

import { geocodingCache } from "./geocodingCache";
import { getEuropeCountryByCode, normalizeCountryCode } from "@shared/europe-countries";

export const PROVINCE_CENTERS: Record<string, { lat: number; lng: number; zoom: number }> = {
  "Noord-Holland": { lat: 52.3792, lng: 4.9003, zoom: 9 },
  "Zuid-Holland": { lat: 52.0705, lng: 4.3007, zoom: 9 },
  "Utrecht": { lat: 52.0907, lng: 5.1214, zoom: 10 },
  "Noord-Brabant": { lat: 51.5656, lng: 5.0913, zoom: 9 },
  "Gelderland": { lat: 52.0116, lng: 5.8987, zoom: 9 },
  "Overijssel": { lat: 52.2435, lng: 6.1944, zoom: 9 },
  "Limburg": { lat: 50.8514, lng: 5.6910, zoom: 9 },
  "Fryslân": { lat: 53.2012, lng: 5.7999, zoom: 9 },
  "Groningen": { lat: 53.2194, lng: 6.5665, zoom: 10 },
  "Drenthe": { lat: 52.7890, lng: 6.8950, zoom: 9 },
  "Flevoland": { lat: 52.5185, lng: 5.4714, zoom: 10 },
  "Zeeland": { lat: 51.4940, lng: 3.8497, zoom: 9 },
};

export const CITY_CENTERS: Record<string, { lat: number; lng: number; zoom: number }> = {
  "amsterdam": { lat: 52.3676, lng: 4.9041, zoom: 12 },
  "rotterdam": { lat: 51.9225, lng: 4.4792, zoom: 12 },
  "utrecht": { lat: 52.0907, lng: 5.1214, zoom: 12 },
  "den haag": { lat: 52.0705, lng: 4.3007, zoom: 12 },
  "s-gravenhage": { lat: 52.0705, lng: 4.3007, zoom: 12 },
  "eindhoven": { lat: 51.4416, lng: 5.4697, zoom: 12 },
  "groningen": { lat: 53.2194, lng: 6.5665, zoom: 12 },
  "almere": { lat: 52.3702, lng: 5.2141, zoom: 12 },
  "tilburg": { lat: 51.5555, lng: 5.0913, zoom: 12 },
  "breda": { lat: 51.5895, lng: 4.7735, zoom: 12 },
  "nijmegen": { lat: 51.8126, lng: 5.8372, zoom: 12 },
  "enschede": { lat: 52.2215, lng: 6.8937, zoom: 12 },
  "apeldoorn": { lat: 52.2112, lng: 5.9699, zoom: 12 },
  "haarlem": { lat: 52.3874, lng: 4.6462, zoom: 12 },
  "arnhem": { lat: 51.9851, lng: 5.8987, zoom: 12 },
  "amersfoort": { lat: 52.1561, lng: 5.3878, zoom: 12 },
  "zaanstad": { lat: 52.4420, lng: 4.8292, zoom: 12 },
  "zaandam": { lat: 52.4420, lng: 4.8292, zoom: 12 },
  "s-hertogenbosch": { lat: 51.6978, lng: 5.3037, zoom: 12 },
  "den bosch": { lat: 51.6978, lng: 5.3037, zoom: 12 },
  "haarlemmermeer": { lat: 52.3030, lng: 4.6855, zoom: 11 },
  "hoofddorp": { lat: 52.3030, lng: 4.6855, zoom: 12 },
  "zwolle": { lat: 52.5125, lng: 6.0830, zoom: 12 },
  "zoetermeer": { lat: 52.0607, lng: 4.4940, zoom: 12 },
  "leeuwarden": { lat: 53.2012, lng: 5.7999, zoom: 12 },
  "leiden": { lat: 52.1601, lng: 4.4970, zoom: 12 },
  "dordrecht": { lat: 51.8133, lng: 4.6901, zoom: 12 },
  "maastricht": { lat: 50.8514, lng: 5.6910, zoom: 12 },
  "ede": { lat: 52.0442, lng: 5.6709, zoom: 12 },
  "emmen": { lat: 52.7858, lng: 6.8976, zoom: 12 },
  "venlo": { lat: 51.3700, lng: 6.1727, zoom: 12 },
  "delft": { lat: 52.0116, lng: 4.3571, zoom: 12 },
  "deventer": { lat: 52.2544, lng: 6.1602, zoom: 12 },
  "alkmaar": { lat: 52.6324, lng: 4.7534, zoom: 12 },
  "helmond": { lat: 51.4792, lng: 5.6570, zoom: 12 },
  "hilversum": { lat: 52.2237, lng: 5.1763, zoom: 12 },
  "amstelveen": { lat: 52.3014, lng: 4.8626, zoom: 12 },
  "oss": { lat: 51.7694, lng: 5.5239, zoom: 12 },
  "velsen": { lat: 52.4583, lng: 4.6254, zoom: 12 },
  "hengelo": { lat: 52.2656, lng: 6.7932, zoom: 12 },
  "heerlen": { lat: 50.8872, lng: 5.9733, zoom: 12 },
  "purmerend": { lat: 52.5030, lng: 4.9602, zoom: 12 },
  "schiedam": { lat: 51.9167, lng: 4.4000, zoom: 12 },
  "roosendaal": { lat: 51.5342, lng: 4.4553, zoom: 12 },
  "lelystad": { lat: 52.5185, lng: 5.4714, zoom: 12 },
  "alphen aan den rijn": { lat: 52.1293, lng: 4.6555, zoom: 12 },
  "hoorn": { lat: 52.6424, lng: 5.0602, zoom: 12 },
  "gouda": { lat: 52.0116, lng: 4.7105, zoom: 12 },
  "bergen op zoom": { lat: 51.4947, lng: 4.2872, zoom: 12 },
  "capelle aan den ijssel": { lat: 51.9288, lng: 4.5802, zoom: 12 },
  "vlaardingen": { lat: 51.9125, lng: 4.3417, zoom: 12 },
  "assen": { lat: 52.9928, lng: 6.5642, zoom: 12 },
  "veenendaal": { lat: 52.0250, lng: 5.5550, zoom: 12 },
  "zeist": { lat: 52.0875, lng: 5.2354, zoom: 12 },
  "nieuwegein": { lat: 52.0303, lng: 5.0861, zoom: 12 },
  "hardenberg": { lat: 52.5761, lng: 6.6189, zoom: 12 },
  "drachten": { lat: 53.1111, lng: 6.1000, zoom: 12 },
  "doetinchem": { lat: 51.9647, lng: 6.2917, zoom: 12 },
};

/**
 * Get center coordinates for a province
 */
export function getProvinceCenter(province: string): { lat: number; lng: number; zoom: number } | null {
  // Case-insensitive lookup
  const key = Object.keys(PROVINCE_CENTERS).find(k => k.toLowerCase() === province.toLowerCase());
  return key ? PROVINCE_CENTERS[key] : null;
}

/**
 * Get center coordinates for a city (approximate)
 * Uses hardcoded list first to save API calls, then cache, then geocoding API
 */
export async function getCityCenter(
  city: string,
  province?: string,
  country?: string
): Promise<{ lat: number; lng: number; zoom: number } | null> {
  const normalizedCity = city.toLowerCase().trim();

  // 1. Check hardcoded list for common cities
  if (CITY_CENTERS[normalizedCity]) {
    console.log(`[Geocoding] Using hardcoded result for "${city}"`);
    return CITY_CENTERS[normalizedCity];
  }

  // 2. Create cache key
  const normalizedCountry = country ? (normalizeCountryCode(country) || country.toUpperCase()) : "ALL";
  const cacheKey = `city:${normalizedCity}${province ? `:${province.toLowerCase()}` : ""}:${normalizedCountry}`;

  // 3. Check cache
  const cached = geocodingCache.get<{ lat: number; lng: number; zoom: number }>(cacheKey);
  if (cached) {
    console.log(`[Geocoding] Using cached result for "${city}"`);
    return cached;
  }

  // 4. Try to geocode the city (last resort)
  try {
    const countryLabel =
      normalizedCountry === "ALL"
        ? ""
        : (getEuropeCountryByCode(normalizedCountry)?.labelEn || normalizedCountry);
    const addressParts = [city, province, countryLabel].filter(Boolean).join(", ");
    const response = await fetch(`/api/geocode?address=${encodeURIComponent(addressParts)}`);
    if (response.ok) {
      const data = await response.json();
      if (data.lat && data.lng) {
        const result = { lat: data.lat, lng: data.lng, zoom: 12 };
        // Cache the result
        geocodingCache.set(cacheKey, result);
        console.log(`[Geocoding] Cached new result from API for "${city}"`);
        return result;
      }
    }
  } catch (error) {
    console.warn("Failed to geocode city, falling back to province center:", error);
  }

  // 5. Fallback to province center if available
  if (province) {
    const provinceCenter = getProvinceCenter(province);
    if (provinceCenter) {
      return { ...provinceCenter, zoom: 11 }; // Slightly more zoomed in for city
    }
  }

  return null;
}
